import { AfterViewInit, Component, OnInit, OnDestroy, ViewChild } from '@angular/core';
import { Router } from '@angular/router';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { MatSort, MatPaginator, MatTableDataSource } from '@angular/material';

import { Subject, combineLatest } from 'rxjs';
import { first, map, takeUntil } from 'rxjs/operators';
import { SessionStorage } from 'ngx-webstorage';

import {
    ApplicationService,
    RequestInfo,
    SearchParameters,
    SchoolInfo,
    PersonInfo,
    CategoryInfo,
} from 'services/application/application.service';
import { ErrorService } from 'services/error/error.service';
import { ScreenSizeService } from 'services/screensize/screensize.service';
import { UserInfo, UserService } from 'services/user/user.service';

@Component({
    selector: 'app-search',
    styleUrls: ['search.component.scss'],
    templateUrl: 'search.component.html',
})
export class SearchComponent implements AfterViewInit, OnInit, OnDestroy {
    @ViewChild('paginator') paginator: MatPaginator;
    @ViewChild(MatSort) sorter: MatSort;

    private _subrelease: Subject<void> = new Subject<void>();

    AllCategories: CategoryInfo[];
    AllHeadersNonBoard: string[] = [
        'RequestIssueNumber',
        'BoardMember',
        'DueDate',
        'Priority',
        'Subject',
        'CategoryLabel',
        'StatusLabel',
        // 'OwnerName',
        'DateCreated',
        // 'DateClosed',
        'Actions',
    ];
    AllHeadersBoard: string[] = [
        'RequestIssueNumber',
        'DueDate',
        'Priority',
        'Subject',
        'CategoryLabel',
        'StatusLabel',
        'DateCreated',
        // 'DateClosed',
        'Actions',
    ];
    AllSchools: SchoolInfo[];
    AllSchoolYears: string[];
    AsBoard: boolean;
    BoardMembers: PersonInfo[];
    Constituents: string[];
    HideReferredTo: boolean;
    @SessionStorage() LastSearchOptions: SearchParameters;
    @SessionStorage() LastSearchResults: RequestInfo[];
    SearchResults: RequestInfo[];
    NoData: boolean;
    ViewReady: boolean;
    ReferredToUsers: PersonInfo[];
    SearchConducted: boolean;
    SearchForm: FormGroup;
    ShowingSearchPanel: boolean;
    SmallDisplay: boolean;
    SearchResultsData: MatTableDataSource<RequestInfo>;
    User: UserInfo;
    VisibleHeaders: string[];
    VisibleColumns: string[];

    constructor(
        private appService: ApplicationService,
        private errorService: ErrorService,
        private fb: FormBuilder,
        private router: Router,
        private screenSizeService: ScreenSizeService,
        private userService: UserService
    ) {}

    ngOnDestroy() {
        this._subrelease.next();
        this._subrelease.complete();
    }

    ngAfterViewInit() {
        if (this.paginator) {
            this.SearchResultsData.paginator = this.paginator;
        }
        if (this.sorter) {
            this.SearchResultsData.sort = this.sorter;
        }
    }

    ngOnInit() {
        const self = this;

        self.AllCategories = null;
        self.AllSchools = null;
        self.AllSchoolYears = null;
        self.AsBoard = false;
        self.BoardMembers = null;
        self.Constituents = null;
        self.HideReferredTo = true;
        self.SearchResults = null;
        self.NoData = false;
        self.ReferredToUsers = null;
        self.SearchConducted = false;
        self.ShowingSearchPanel = true;
        self.User = null;
        self.ViewReady = false;
        self.SearchResultsData = new MatTableDataSource<RequestInfo>([]);

        self.SmallDisplay = self.screenSizeService.ScreenSize.IsSmallSize;

        self.screenSizeService.ScreenSizeSub.pipe(takeUntil(self._subrelease)).subscribe(size => {
            self.SmallDisplay = size.IsSmallSize;

            self.VisibleHeaders = self.SmallDisplay
                ? ['SmallDisplay']
                : self.HideReferredTo
                ? self.AllHeadersBoard.slice()
                : self.AllHeadersNonBoard.slice();
            self.VisibleColumns = self.NoData ? ['NoData'] : self.VisibleHeaders.slice();
        });

        // Make the search form group
        self.SearchForm = self.fb.group({
            srchYear: [''],
            srchIssueNum: ['', Validators.pattern(/(^\d*$)|(^\d*\-\d+$)/)],
            srchType: [0],
            srchDateBegin: [null],
            srchDateEnd: [null],
            srchSubject: [''],
            srchSchool: [''],
            srchReferredTo: [''],
            srchBoardMember: [null],
            SearchSource: [''],
            srchCategory: [null],
            srchDistrict: [''],
            srchPriority: [null],
            srchUnassignedOnly: [false],
        });

        // Get the first non-null UserInfo object so we can begin setting up the component
        self.userService.UserSubject.pipe(
            takeUntil(self._subrelease),
            first(user => user && !!user.AuthToken)
        ).subscribe(user => {
            // Set up constant monitoring of the user object, to catch AuthToken updates.
            self.userService.UserSubject.pipe(takeUntil(self._subrelease)).subscribe(_user => {
                self.User = _user;
                if (self.User) {
                    self.AsBoard = self.User.CurrentRole === 'BoardMember' || self.User.CurrentRole === 'BoardAssistant';
                    self.HideReferredTo =
                        !self.User || self.User.CurrentRole === 'BoardMember' || self.User.CurrentRole === 'BoardAssistant';
                }
            });

            // Load data needed for the component
            const allSchoolsObs = self.appService.GetAllSchools();
            const allSchoolYearsObs = self.appService.GetAllSchoolYears();
            const boardObs = self.appService.GetAllBoardMembers();
            const constituentsObs = self.appService.GetAllConstituents(self.User.AuthToken);
            const referredToUsersObs = self.appService.GetAllReferredToUsers(self.User.AuthToken);
            const allCategoriesObs = self.appService.GetAllCategories();

            combineLatest(allSchoolsObs, allSchoolYearsObs, boardObs, constituentsObs, referredToUsersObs, allCategoriesObs)
                .pipe(
                    takeUntil(self._subrelease),
                    map(_packet => {
                        return {
                            Schools: _packet[0],
                            Years: _packet[1],
                            BoardMembers: _packet[2],
                            Constituents: _packet[3],
                            ReferredToUsers: _packet[4],
                            Categories: _packet[5],
                        };
                    })
                )
                .subscribe(
                    packet => {
                        self.AllSchools = packet.Schools;
                        self.AllSchoolYears = packet.Years;
                        self.BoardMembers = packet.BoardMembers;
                        self.Constituents = packet.Constituents;
                        self.ReferredToUsers = packet.ReferredToUsers;
                        self.AllCategories = packet.Categories;

                        // Is there cached search parameters and/or results from a previous run for this user? If so, default to that now.
                        if (self.LastSearchOptions) {
                            self.SearchForm.patchValue(self.LastSearchOptions);
                        }

                        if (self.LastSearchResults && !!self.LastSearchResults.length) {
                            self.SearchConducted = true;
                            self.SearchResults = self.LastSearchResults.slice();
                            self.NoData = !self.SearchResults.length;
                            self.SearchResultsData.data = self.NoData ? [<RequestInfo>{}] : self.SearchResults;

                            self.VisibleHeaders = self.SmallDisplay
                                ? ['SmallDisplay']
                                : self.HideReferredTo
                                ? self.AllHeadersBoard.slice()
                                : self.AllHeadersNonBoard.slice();
                            self.VisibleColumns = self.NoData ? ['NoData'] : self.VisibleHeaders.slice();
                        }

                        self.ViewReady = true;
                    },
                    error => self.errorService.HandleServiceError(error)
                );
        });
    }

    // #region Clear Search Form

    ClearSearchForm() {
        this.SearchForm.reset();
    }

    // #endregion

    // #region Handle Search

    HandleSearch() {
        const self = this;

        const searchValues = <SearchParameters>self.SearchForm.getRawValue();

        // Before sending off the search request, check to see if we're using a specific type of search (board or constituent).
        // If it's board, we need to clear out the constituent search field and move the value which was in there (SAPID) into the created by field.
        if (searchValues.srchType === 0) {
            searchValues.srchRequestedBy = searchValues.srchBoardMember && searchValues.srchBoardMember.SAPID;
            searchValues.srchSource = '';
        }

        // Also, check the issue number being requested (if any). If it was entered using the year-issue format (ex: 1920-006), strip away the
        // year portion and make that the search year, and use the simpler version of the issue number.
        if (searchValues.srchIssueNum) {
            if (searchValues.srchIssueNum.indexOf('-') >= 0) {
                const issueParts = searchValues.srchIssueNum.split('-');

                if (issueParts[0].length === 4) {
                    searchValues.srchYear = `20${issueParts[0].substr(0, 2)}20${issueParts[0].substr(2)}`;
                }

                searchValues.srchIssueNum = '' + +issueParts[1];
            }
        }

        self.appService.PerformSearch(self.User.AuthToken, self.User.CurrentRole, searchValues).subscribe(
            results => {
                self.SearchResults = results || <RequestInfo[]>[];
                self.NoData = !self.SearchResults.length;
                self.SearchResultsData.data = self.NoData ? [<RequestInfo>{}] : self.SearchResults;

                self.VisibleHeaders = self.SmallDisplay
                    ? ['SmallDisplay']
                    : self.HideReferredTo
                    ? self.AllHeadersBoard.slice()
                    : self.AllHeadersNonBoard.slice();
                self.VisibleColumns = self.NoData ? ['NoData'] : self.VisibleHeaders.slice();

                self.SearchConducted = true;

                // Re-hide the search options if we're on small screen size
                if (self.SmallDisplay) {
                    self.ShowingSearchPanel = false;
                }

                // Backup the most recent search parameters and results
                self.LastSearchOptions = searchValues;
                self.LastSearchResults = self.SearchResults.slice();
            },
            error => self.errorService.HandleServiceError(error)
        );
    }

    // #endregion

    // #region Handle View

    HandleView(req: RequestInfo) {
        const self = this;

        if (!req || req.RequestID == null) {
            return;
        }

        let route = [];

        // If the current user role is BoardMember or BoardAssistant, all they can do is view the request.
        if (self.User.CurrentRole === 'BoardMember' || self.User.CurrentRole === 'BoardAssistant') {
            route = ['/requestview', req.RequestID, 'search'];
        } else if (req.Status === 'ACTIVE' && (self.User.CurrentRole === 'Facilitator' || self.User.CurrentRole === 'Develoepr')) {
            // This is an unassigned request, and the current user is a facilitator or developer, so send them to the edit handler.
            route = ['/edit', req.RequestID, 'search'];
        } else {
            // All other route options go to the handlerequest route.
            route = ['/handlerequest', req.RequestID, 'search'];
        }

        self.router.navigate(route);
    }

    // #endregion

    // #region Toggle Search Panel

    ToggleSearchPanel() {
        this.ShowingSearchPanel = !this.ShowingSearchPanel;
    }

    // #endregion
}
